import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Stack, Typography, Box, alpha, useTheme,
  TextField, InputAdornment, Alert, Paper, Chip,
  CircularProgress, Divider,
} from '@mui/material';
import {
  ReceiptLong, CheckCircle, ErrorOutline, ContentCopy,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';

import feeReconciliationService from '../../services/feeReconciliationService';
import { useAuth } from '../../context/AuthContext';
import { BankRow, ReconciliationRowResult } from '../../types/fees';
import { EXCEPTION_LABEL } from '../../utils/reconciliationCsv';
import {
  isValidChallanNumber, normalizeChallanNumber, CHALLAN_NUMBER_LENGTH,
} from '../../utils/challanNumber';
import { useCurrencyFormat } from '../../hooks/useCurrencyFormat';

interface Props {
  open: boolean;
  onClose: () => void;
  onRecorded?: (row: ReconciliationRowResult) => void;
}

/**
 * ManualReconciliationDialog — record a single bank payment without a CSV.
 *
 * Wraps the row as a one-element `[{...}]` and hits the same
 * `/fee-reconciliation/commit` endpoint the batch flow uses, so classification,
 * validation, allocation-with-arrears, ledger posting, and duplicate-ref
 * guarding all flow through one code path. Zero new backend surface.
 *
 * Client-side we guard the obvious typos (bad Luhn, non-positive amount,
 * future-dated txn) so we can give an instant nudge rather than a network
 * round-trip.
 */
const ManualReconciliationDialog: React.FC<Props> = ({ open, onClose, onRecorded }) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { currency } = useAuth();

  const today = useMemo(() => format(new Date(), 'yyyy-MM-dd'), []);

  const [challanNumber, setChallanNumber] = useState('');
  const [amount, setAmount] = useState('');
  const [txnDate, setTxnDate] = useState(today);
  const [bankRef, setBankRef] = useState('');
  const [paidByName, setPaidByName] = useState('');
  const [memo, setMemo] = useState('');
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<ReconciliationRowResult | null>(null);
  const [genericError, setGenericError] = useState<string | null>(null);

  const softBorder = theme.palette.divider;

  const currencyFmt = useCurrencyFormat();

  // Reset all state whenever the dialog is (re)opened. The parent controls
  // lifetime — we just make sure the form is pristine each time.
  useEffect(() => {
    if (!open) return;
    setChallanNumber('');
    setAmount('');
    setTxnDate(today);
    setBankRef('');
    setPaidByName('');
    setMemo('');
    setSaving(false);
    setResult(null);
    setGenericError(null);
  }, [open, today]);

  // ─── Field-level derived state ─────────────────────────────────────────────

  const parsedAmount = Number(amount);
  const challanLenOk = challanNumber.length === CHALLAN_NUMBER_LENGTH;
  const challanLuhnOk = isValidChallanNumber(challanNumber);
  const showChallanError = challanNumber.length > 0 && challanLenOk && !challanLuhnOk;

  const amountOk = Number.isFinite(parsedAmount) && parsedAmount > 0;
  const dateOk = !!txnDate && txnDate <= today;
  const bankRefOk = bankRef.trim().length > 0;

  const canSubmit = challanLuhnOk && amountOk && dateOk && bankRefOk && !saving && !result;

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const onChallanChange = (raw: string) => {
    // Digit-only, capped at 10. Paste-friendly (strips spaces, hyphens).
    setChallanNumber(normalizeChallanNumber(raw).slice(0, CHALLAN_NUMBER_LENGTH));
    setResult(null);
    setGenericError(null);
  };

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    setGenericError(null);
    try {
      const row: BankRow = {
        challanNumber,
        amount: Math.round(parsedAmount * 100) / 100,
        txnDate,
        bankRef: bankRef.trim(),
        paidByName: paidByName.trim() || undefined,
        memo: memo.trim() || undefined,
      };
      const res = await feeReconciliationService.commitReconciliation([row]);
      const first = res.rows[0];
      setResult(first);
      if (first?.status === 'matched') {
        enqueueSnackbar(
          first.receiptNumber
            ? `Payment recorded · Receipt ${first.receiptNumber}`
            : 'Payment recorded',
          { variant: 'success' },
        );
        onRecorded?.(first);
      }
    } catch (e: any) {
      setGenericError(e?.message || 'Failed to record payment');
    } finally {
      setSaving(false);
    }
  };

  const copyReceipt = async () => {
    if (!result?.receiptNumber) return;
    try {
      await navigator.clipboard.writeText(result.receiptNumber);
      enqueueSnackbar('Receipt number copied', { variant: 'success' });
    } catch {
      /* clipboard blocked — ignore silently */
    }
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  const isSuccess = result?.status === 'matched';
  const isException = result?.status === 'exception';

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle component="div" sx={{ pb: 2 }}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <ReceiptLong color="primary" fontSize="small" />
          <Typography variant="h6" fontWeight={700}>Record a bank payment</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Use this for a single deposit that arrived outside a settlement file. Runs the same match-and-record pipeline the batch flow does.
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {/* Success state — replace the form with a receipt card. */}
        {isSuccess ? (
          <Paper
            variant="outlined"
            sx={{
              p: 2.5, borderRadius: 2,
              borderColor: alpha(theme.palette.success.main, 0.32),
              bgcolor: alpha(theme.palette.success.main, 0.04),
            }}
          >
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1.25} alignItems="center">
                <CheckCircle sx={{ color: theme.palette.success.main }} />
                <Typography variant="subtitle1" fontWeight={700}>
                  Payment recorded
                </Typography>
              </Stack>

              <Divider sx={{ borderColor: alpha(theme.palette.success.main, 0.18) }} />

              <Stack spacing={1}>
                <FieldRow label="Student" value={result?.studentName ?? '—'} />
                <FieldRow
                  label="Challan"
                  value={result?.challanNumber ?? '—'}
                  mono
                />
                <FieldRow label="Amount" value={currencyFmt(result?.amount ?? 0)} mono />
                {result?.arrearsCount != null && result.arrearsCount > 0 && (
                  <Box>
                    <Chip
                      size="small"
                      label={`Split across ${result.arrearsCount} prior challan${result.arrearsCount === 1 ? '' : 's'} + this one`}
                      variant="outlined"
                      sx={{
                        height: 22, fontSize: '0.7rem', fontWeight: 600,
                        borderColor: alpha(theme.palette.warning.main, 0.5),
                        color: theme.palette.warning.dark,
                        bgcolor: alpha(theme.palette.warning.main, 0.08),
                      }}
                    />
                  </Box>
                )}
                {result?.receiptNumber && (
                  <FieldRow
                    label="Receipt"
                    value={
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.95rem' }}>
                          {result.receiptNumber}
                        </Typography>
                        <Button
                          size="small"
                          onClick={copyReceipt}
                          startIcon={<ContentCopy sx={{ fontSize: '0.85rem !important' }} />}
                          sx={{ minWidth: 0, px: 1, py: 0.25, fontSize: '0.72rem' }}
                        >
                          Copy
                        </Button>
                      </Stack>
                    }
                  />
                )}
              </Stack>
            </Stack>
          </Paper>
        ) : (
          <Stack spacing={2} pt={1}>
            {/* Challan + amount pair — most important, side-by-side on wide viewports. */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Challan number"
                placeholder="0000000000"
                value={challanNumber}
                onChange={e => onChallanChange(e.target.value)}
                disabled={saving}
                required
                autoFocus
                fullWidth
                inputProps={{
                  inputMode: 'numeric',
                  maxLength: CHALLAN_NUMBER_LENGTH,
                  style: { fontFamily: 'monospace', letterSpacing: 1, fontVariantNumeric: 'tabular-nums' },
                }}
                error={showChallanError}
                helperText={
                  showChallanError
                    ? "That doesn't look like a valid challan number. Double-check for a typo."
                    : `${challanNumber.length}/${CHALLAN_NUMBER_LENGTH} digits`
                }
                InputProps={{
                  endAdornment: challanLuhnOk ? (
                    <InputAdornment position="end">
                      <CheckCircle sx={{ fontSize: 18, color: theme.palette.success.main }} />
                    </InputAdornment>
                  ) : undefined,
                }}
              />
              <TextField
                label="Amount"
                type="number"
                value={amount}
                onChange={e => setAmount(e.target.value)}
                disabled={saving}
                required
                fullWidth
                inputProps={{ min: 0, step: 100 }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">{currency}</InputAdornment>,
                  sx: { '& input': { fontVariantNumeric: 'tabular-nums' } },
                }}
              />
            </Stack>

            {/* Date + bank ref — bank-side facts. */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Transaction date"
                type="date"
                value={txnDate}
                onChange={e => setTxnDate(e.target.value)}
                disabled={saving}
                required
                fullWidth
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: today }}
                error={!!txnDate && !dateOk}
                helperText={!!txnDate && !dateOk ? 'Date cannot be in the future.' : ' '}
              />
              <TextField
                label="Bank reference"
                placeholder="e.g. HBL-TX-9982371"
                value={bankRef}
                onChange={e => setBankRef(e.target.value)}
                disabled={saving}
                required
                fullWidth
                helperText="Blocks duplicate imports."
              />
            </Stack>

            <Divider sx={{ borderColor: softBorder }}>
              <Typography variant="caption" color="text.secondary" sx={{ px: 1, letterSpacing: 0.4 }}>
                OPTIONAL
              </Typography>
            </Divider>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Depositor name"
                placeholder="Who paid?"
                value={paidByName}
                onChange={e => setPaidByName(e.target.value)}
                disabled={saving}
                fullWidth
                inputProps={{ maxLength: 120 }}
              />
              <TextField
                label="Memo"
                placeholder="Free-form note"
                value={memo}
                onChange={e => setMemo(e.target.value)}
                disabled={saving}
                fullWidth
                inputProps={{ maxLength: 240 }}
              />
            </Stack>

            {/* Inline exception — from the backend, e.g. AMOUNT_MISMATCH. */}
            {isException && result && (
              <Alert
                severity="warning"
                variant="outlined"
                icon={<ErrorOutline />}
                sx={{ borderRadius: 1.5 }}
              >
                <Typography variant="body2" fontWeight={700}>
                  {result.exceptionCode ? EXCEPTION_LABEL[result.exceptionCode] : 'Could not record'}
                </Typography>
                {result.exceptionMessage && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                    {result.exceptionMessage}
                  </Typography>
                )}
                {result.outstanding != null && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                    Outstanding on this challan: <strong>{currencyFmt(result.outstanding)}</strong>
                  </Typography>
                )}
              </Alert>
            )}

            {/* Transport/unexpected error — network drop, 500, etc. */}
            {genericError && (
              <Alert severity="error" variant="outlined" sx={{ borderRadius: 1.5 }}>
                {genericError}
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        {isSuccess ? (
          <Button
            variant="contained"
            disableElevation
            onClick={onClose}
            sx={{ borderRadius: 1.5, minWidth: 120, fontWeight: 700 }}
          >
            Done
          </Button>
        ) : (
          <>
            <Button onClick={onClose} disabled={saving}>Cancel</Button>
            <Button
              variant="contained"
              disableElevation
              onClick={submit}
              disabled={!canSubmit}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{ borderRadius: 1.5, minWidth: 180, fontWeight: 700 }}
            >
              {saving ? 'Recording…' : 'Record payment'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

// ─── Small internal helpers ──────────────────────────────────────────────────

interface FieldRowProps {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}

const FieldRow: React.FC<FieldRowProps> = ({ label, value, mono }) => (
  <Stack direction="row" spacing={2} alignItems="baseline">
    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 96, letterSpacing: 0.4, textTransform: 'uppercase', fontWeight: 600 }}>
      {label}
    </Typography>
    {typeof value === 'string' || typeof value === 'number' ? (
      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          fontFamily: mono ? 'monospace' : undefined,
          fontVariantNumeric: mono ? 'tabular-nums' : undefined,
        }}
      >
        {value}
      </Typography>
    ) : (
      value
    )}
  </Stack>
);

export default ManualReconciliationDialog;
